import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  GenericSelect,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'
import { McLeodSyncSection } from './mcleod/mcleod-sync-section'
import { McLeodWorkspaceIDSection } from './mcleod/mcleod-workspace-id-section'
import { useMcLeodMutations } from './mcleod/use-mcleod-mutations'

type McLeodModuleType = 'DFM' | 'API'

const MODULE_TYPE_OPTIONS = [
  { label: 'DFM (Digital Freight Matching)', value: 'DFM' },
  { label: 'API Module', value: 'API' }
]

const TIMEZONE_OPTIONS = [
  { label: 'Pacific Time', value: 'PT' },
  { label: 'Mountain Time', value: 'MT' },
  { label: 'Central Time', value: 'CT' },
  { label: 'Eastern Time', value: 'ET' }
]

const dfmFormSchema = z.object({
  companyID: z.string().min(1, 'Company ID is required'),
  mcLeodInstanceURL: z.string().min(1, 'McLeod instance URL is required'),
  mcLeodServerTimezone: z.string().min(1, 'McLeod server timezone is required'),
  mcLeodVersion: z
    .string()
    .min(1, 'McLeod Version is required')
    .regex(/^(0[0-9]|[1-9][0-9])\.[12]$/, 'Version must be in format YY.1 (spring) or YY.2 (fall), e.g. 24.1 or 24.2'),
  password: z.string().min(1, 'Password is required'),
  username: z.string().min(1, 'Username is required')
})

const apiFormSchema = z.object({
  companyID: z.string().min(1, 'Company ID is required'),
  mcLeodInstanceURL: z.string().min(1, 'McLeod instance URL is required'),
  mcLeodServerTimezone: z.string().min(1, 'McLeod server timezone is required'),
  password: z.string().min(1, 'Password is required'),
  username: z.string().min(1, 'Username is required')
})

/**
 * Modal for the McLeod TMS app, supporting both DFM and API modules.
 */
export function McLeodTmsModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [externalWorkspaceID, setExternalWorkspaceID] = useState<string | null>(
    app?.workspaceApp?.appCredentials?.chaineAPIToken || null
  )

  const detectedModuleType: McLeodModuleType = appType === 'McLeodAPIModule' ? 'API' : 'DFM'
  const [moduleType, setModuleType] = useState<McLeodModuleType>(detectedModuleType)

  const isExistingApp = !!app?.workspaceApp?.appCredentials && moduleType === detectedModuleType
  const isDFM = moduleType === 'DFM'
  const currentSchema = isDFM ? dfmFormSchema : apiFormSchema

  const form = useForm<z.infer<typeof dfmFormSchema>>({
    defaultValues: {
      companyID: '',
      mcLeodInstanceURL: '',
      mcLeodServerTimezone: '',
      mcLeodVersion: '',
      password: '',
      username: ''
    },
    resolver: zodResolver(currentSchema)
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const { enableMcleodAPIApp, enableMcleodApp, handleSyncRequest, isLoading, isSyncing, updateMcleodAPIApp } =
    useMcLeodMutations({ handleClose, setExternalWorkspaceID, setFormError, workspaceID })

  useEffect(() => {
    if (app?.workspaceApp?.appCredentials) {
      form.reset({
        companyID: app.workspaceApp.appMetadata?.companyID || '',
        mcLeodInstanceURL: app.workspaceApp.appMetadata?.mcLeodInstanceURL || '',
        mcLeodServerTimezone: app.workspaceApp.appMetadata?.mcLeodServerTimezone || '',
        mcLeodVersion: app.workspaceApp.appMetadata?.mcLeodVersion?.toString() || '',
        password: app.workspaceApp.appCredentials.password || '',
        username: app.workspaceApp.appCredentials.username || ''
      })
    }
  }, [app, form])

  const onSubmit = (values: z.infer<typeof dfmFormSchema>) => {
    if (isDFM) {
      enableMcleodApp({
        variables: {
          companyID: values.companyID,
          mcLeodInstanceURL: values.mcLeodInstanceURL,
          mcLeodServerTimezone: values.mcLeodServerTimezone,
          mcLeodVersion: parseFloat(values.mcLeodVersion),
          password: values.password,
          username: values.username
        }
      })
    } else if (isExistingApp) {
      updateMcleodAPIApp({
        variables: {
          companyID: values.companyID,
          mcLeodInstanceURL: values.mcLeodInstanceURL,
          mcLeodServerTimezone: values.mcLeodServerTimezone,
          password: values.password,
          username: values.username
        }
      })
    } else {
      enableMcleodAPIApp({
        variables: {
          companyID: values.companyID,
          mcLeodInstanceURL: values.mcLeodInstanceURL,
          mcLeodServerTimezone: values.mcLeodServerTimezone,
          password: values.password,
          username: values.username
        }
      })
    }
  }

  const handleModuleTypeChange = (value: string) => {
    setModuleType(value as McLeodModuleType)
    setFormError('')
    form.reset()
  }

  const displayedWorkspaceID = app?.workspaceApp?.externalWorkspaceID || externalWorkspaceID

  let submitButtonLabel = 'Enable'
  if (isLoading) submitButtonLabel = 'Processing...'
  else if (isExistingApp) submitButtonLabel = 'Update'

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable McLeod for Chain</DialogTitle>
          <DialogDescription>
            Enter the McLeod TMS credentials created for the Chain User. Refer to the integration guide for more
            information.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6 w-full">
            <div className="w-full">
              <FormLabel>Module Type</FormLabel>
              <GenericSelect
                triggerClassName="w-full mt-2"
                value={moduleType}
                onValueChange={handleModuleTypeChange}
                options={MODULE_TYPE_OPTIONS}
                placeholder="Select module type"
                disabled={isExistingApp || isLoading}
              />
            </div>

            <FormField
              control={form.control}
              name="mcLeodInstanceURL"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>McLeod Instance URL</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="Enter Your McLeod TMS instance URL (e.g. https://tms-XXXXX.mcleodhosted.com)"
                      {...field}
                      disabled={isLoading}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyID"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Company ID</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="Enter McLeod company ID (such as TMS, TMS2, etc.)"
                      {...field}
                      disabled={isLoading}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!isDFM && (
              <FormField
                control={form.control}
                name="mcLeodVersion"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>McLeod Version</FormLabel>
                    <FormControl>
                      <Input
                        className="w-full"
                        placeholder="Enter McLeod version (YY.1 for spring or YY.2 for fall)"
                        {...field}
                        disabled={isLoading}
                        value={field.value}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="mcLeodServerTimezone"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>McLeod Server Timezone</FormLabel>
                  <FormControl>
                    <GenericSelect
                      triggerClassName="w-full"
                      value={field.value || ''}
                      onValueChange={(value) => field.onChange(value)}
                      options={TIMEZONE_OPTIONS}
                      placeholder="Enter McLeod Local Server Timezone"
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="Enter McLeod TMS username"
                      {...field}
                      disabled={isLoading}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      type="password"
                      placeholder="Enter McLeod TMS password"
                      {...field}
                      disabled={isLoading}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            {!!displayedWorkspaceID && (
              <McLeodWorkspaceIDSection displayedWorkspaceID={displayedWorkspaceID} isDFM={isDFM} />
            )}

            {!!isDFM && !!isExistingApp && (
              <McLeodSyncSection
                isSyncing={isSyncing}
                onSyncRequest={() => handleSyncRequest(appType as AppTypeEnum)}
              />
            )}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://help.trychain.com/en/articles/10693778-enabling-mcleod-tms"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                Read the integration guide
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading} onClick={form.handleSubmit(onSubmit)}>
                  {/* languagetools:disable-next-line */}
                  {submitButtonLabel}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
